import {
  Box,
  Button,
  Eyebrow,
  Hidden,
  Icon,
  IconTile,
  Inline,
  MenuRow,
  MenuSheetContent,
  NavButton,
  Sheet,
  SheetClose,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
  Spacer,
  Stack,
} from "@generalapplications/ui";
import { Menu } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { isCallToAction, primaryNavigation } from "@/content/navigation";

import {
  sectionPages,
  type SectionPage,
} from "@/app/common/functions/routes/sectionNavigation";

type RowProps = { to: string; children: ReactNode };

/** One destination row, closing the menu as it is followed. */
function Row({ to, children }: RowProps) {
  return (
    <SheetClose>
      <MenuRow>
        <Link to={to}>{children}</Link>
      </MenuRow>
    </SheetClose>
  );
}

/** A section's page as a row: its tile in its colour, then its title. */
function PageRow({ page }: { page: SectionPage }) {
  return (
    <SheetClose>
      <MenuRow>
        <Link to={page.href} data-tone={page.tone}>
          <Inline as="span" gap="sm" align="center">
            <IconTile icon={page.icon} tone="section" size="sm" />
            {page.title}
          </Inline>
        </Link>
      </MenuRow>
    </SheetClose>
  );
}

/**
 * The phone menu: a full-height sheet, every destination a 48 px row, the
 * sections as plain labels with their pages listed under each (the same marks
 * and colours the desktop panels draw), Contact as the closing button in the
 * site's signal colour.
 */
export function MobileNavigation() {
  const [open, setOpen] = useState(false);
  const contact = primaryNavigation.find(isCallToAction);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <Hidden above="md">
        <SheetTrigger>
          <NavButton size="lg" iconOnly aria-label="Open menu">
            <Icon icon={Menu} size="lg" />
          </NavButton>
        </SheetTrigger>
      </Hidden>
      <MenuSheetContent>
        <SheetTitle visuallyHidden>Menu</SheetTitle>
        <SheetDescription visuallyHidden>Site navigation.</SheetDescription>
        {primaryNavigation
          .filter((item) => item !== contact)
          .map((item) => {
            const pages = sectionPages[item.href];
            if (!pages)
              return (
                <Row key={item.href} to={item.href}>
                  {item.label}
                </Row>
              );
            return (
              <Stack
                key={item.href}
                as="nav"
                aria-label={item.label}
                gap="none"
              >
                <Box paddingX="xs" paddingBottom="xs">
                  <Eyebrow tone="muted" as="div">
                    {item.label}
                  </Eyebrow>
                </Box>
                {pages.map((page) => (
                  <PageRow key={page.href} page={page} />
                ))}
                <Row to={item.href}>{item.label} overview</Row>
              </Stack>
            );
          })}
        <Spacer />
        {contact && (
          <SheetClose>
            <Button
              variant="outline"
              tone="section"
              size="xl"
              radius="xl"
              href={contact.href}
            >
              <Icon icon={contact.icon} size="lg" />
              {contact.label}
            </Button>
          </SheetClose>
        )}
      </MenuSheetContent>
    </Sheet>
  );
}
